import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { 
  Upload, 
  CheckCircle2, 
  FileText, 
  Users, 
  Globe, 
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Loader2,
  Clock,
  Building2,
  Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JoinTalentPool = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    profession: '',
    specialisation: '',
    country: '',
    yearsExperience: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf', 
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      const allowedExtensions = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PDF or Word document (.pdf, .doc, .docx)',
          variant: 'destructive'
        });
        e.target.value = ''; // Reset the input
        return;
      }
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Please upload a file smaller than 10MB',
          variant: 'destructive'
        });
        e.target.value = ''; // Reset the input
        return;
      }
      setCvFile(file);
      toast({
        title: 'File selected',
        description: `${file.name} ready to upload`,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone ||
        !formData.profession || !formData.specialisation || !formData.country || !formData.yearsExperience) {
      toast({
        title: 'Missing Fields',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }
    
    if (!cvFile) {
      toast({
        title: 'CV Required',
        description: 'Please upload your CV to continue',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('first_name', formData.firstName.trim());
      formDataToSend.append('last_name', formData.lastName.trim());
      formDataToSend.append('email', formData.email.trim().toLowerCase());
      formDataToSend.append('phone', formData.phone.trim());
      formDataToSend.append('profession', formData.profession);
      formDataToSend.append('specialisation', formData.specialisation.trim());
      formDataToSend.append('country', formData.country);
      formDataToSend.append('years_experience', formData.yearsExperience);
      formDataToSend.append('cv_file', cvFile);

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(`${API_URL}/api/talent-pool/submit`, {
        method: 'POST',
        body: formDataToSend,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit');
      }

      setReferenceId(data.reference_id);
      setSubmitted(true);
      
      toast({
        title: 'Success!',
        description: 'Your CV has been submitted successfully',
      });

    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = 'Please try again later';
      
      if (error.name === 'AbortError') {
        errorMessage = 'Upload timed out. Please try with a smaller file or check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Submission Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
        <Navbar />
        
        <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-24 h-24 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-xl">
              <CheckCircle2 className="h-12 w-12 text-white" />
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              You&apos;re In Our Talent Pool!
            </h1>
            
            <p className="text-xl text-gray-600 mb-8">
              Thank you for joining Luma Med Global. Our medical recruitment team will review your CV and contact you within 48 hours.
            </p>
            
            <Card className="bg-white/80 backdrop-blur border-2 border-teal-200 mb-8">
              <CardContent className="p-6">
                <p className="text-sm text-gray-600 mb-2">Your Reference Number</p>
                <p className="text-3xl font-bold text-teal-600 font-mono">{referenceId}</p>
                <p className="text-xs text-gray-500 mt-2">Save this for your records</p>
              </CardContent>
            </Card>
            
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Card className="bg-white/60 border-gray-200">
                <CardContent className="p-4 text-center">
                  <Clock className="h-8 w-8 text-teal-600 mx-auto mb-2" />
                  <p className="font-semibold text-gray-900">48 Hour Response</p>
                  <p className="text-xs text-gray-600">We&apos;ll review your CV quickly</p>
                </CardContent>
              </Card>
              <Card className="bg-white/60 border-gray-200">
                <CardContent className="p-4 text-center">
                  <ShieldCheck className="h-8 w-8 text-teal-600 mx-auto mb-2" />
                  <p className="font-semibold text-gray-900">Medical Review</p>
                  <p className="text-xs text-gray-600">Reviewed by clinicians</p>
                </CardContent>
              </Card>
              <Card className="bg-white/60 border-gray-200">
                <CardContent className="p-4 text-center">
                  <Building2 className="h-8 w-8 text-teal-600 mx-auto mb-2" />
                  <p className="font-semibold text-gray-900">Top Hospitals</p>
                  <p className="text-xs text-gray-600">Access to premium roles</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button variant="outline" className="px-8">
                  Return Home
                </Button>
              </Link>
              <Link to="/register/candidate">
                <Button className="bg-teal-600 hover:bg-teal-700 px-8">
                  Create Full Account
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur border border-teal-200 px-4 py-2 rounded-full mb-6">
              <Sparkles className="h-4 w-4 text-teal-600" />
              <span className="text-sm font-medium text-teal-700">Building Our Talent Network</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Join Our{' '}
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                Talent Pool
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Upload your CV and connect with healthcare employers across Saudi Arabia and the Middle East.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Benefits Sidebar */}
            <div className="lg:col-span-2 space-y-4">
              <Card className="bg-white/80 backdrop-blur border-2 border-teal-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-4">Why Join Our Talent Pool?</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <ShieldCheck className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Medically-Led Review</p>
                        <p className="text-sm text-gray-600">Your CV is reviewed by medical professionals who understand your expertise</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Healthcare Employer Network</p>
                        <p className="text-sm text-gray-600">Connect with hospitals and healthcare facilities across Saudi Arabia and the Middle East</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Globe className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Relocation Support</p>
                        <p className="text-sm text-gray-600">Guidance on SCFHS, visa, accommodation, and more</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Star className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Competitive Packages</p>
                        <p className="text-sm text-gray-600">Tax-free salaries, housing, flights, and benefits</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Our Commitment */}
              <Card className="bg-gradient-to-br from-teal-600 to-cyan-600 text-white border-0">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Users className="h-8 w-8" />
                    <div>
                      <p className="text-lg font-bold">Growing Talent Network</p>
                      <p className="text-sm opacity-90">Building quality connections</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <p className="text-sm opacity-90 leading-relaxed">
                      As an early-stage platform founded by medical professionals, we&apos;re focused on building 
                      a quality network with transparency and integrity at our core.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Form Section */}
            <div className="lg:col-span-3">
              <Card className="bg-white/90 backdrop-blur border-2 border-gray-200 shadow-xl">
                <CardContent className="p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                      <FileText className="h-6 w-6 text-teal-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Submit Your CV</h2>
                      <p className="text-gray-600">Takes less than 2 minutes</p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="Sarah"
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Johnson"
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {/* Contact */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="sarah@example.com"
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+44 7700 900000"
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {/* Profession */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="profession">Profession *</Label>
                        <Select 
                          onValueChange={(value) => setFormData({...formData, profession: value})} 
                          required
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select your profession" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Registered Nurse">Registered Nurse</SelectItem>
                            <SelectItem value="Medical Doctor">Medical Doctor</SelectItem>
                            <SelectItem value="Specialist Physician">Specialist Physician</SelectItem>
                            <SelectItem value="Surgeon">Surgeon</SelectItem>
                            <SelectItem value="Midwife">Midwife</SelectItem>
                            <SelectItem value="Physiotherapist">Physiotherapist</SelectItem>
                            <SelectItem value="Radiologist">Radiologist</SelectItem>
                            <SelectItem value="Lab Technician">Lab Technician</SelectItem>
                            <SelectItem value="Pharmacist">Pharmacist</SelectItem>
                            <SelectItem value="Other Allied Health">Other Allied Health</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="specialisation">Specialisation *</Label>
                        <Input
                          id="specialisation"
                          name="specialisation"
                          value={formData.specialisation}
                          onChange={handleChange}
                          placeholder="e.g., Critical Care, Cardiology"
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {/* Location & Experience */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="country">Current Country *</Label>
                        <Select 
                          onValueChange={(value) => setFormData({...formData, country: value})} 
                          required
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                            <SelectItem value="Ireland">Ireland</SelectItem>
                            <SelectItem value="United States">United States</SelectItem>
                            <SelectItem value="Canada">Canada</SelectItem>
                            <SelectItem value="Australia">Australia</SelectItem>
                            <SelectItem value="New Zealand">New Zealand</SelectItem>
                            <SelectItem value="South Africa">South Africa</SelectItem>
                            <SelectItem value="India">India</SelectItem>
                            <SelectItem value="Philippines">Philippines</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="yearsExperience">Years of Experience *</Label>
                        <Input
                          id="yearsExperience"
                          name="yearsExperience"
                          type="number"
                          value={formData.yearsExperience}
                          onChange={handleChange}
                          placeholder="e.g., 5"
                          required
                          min="0"
                          max="50"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {/* CV Upload */}
                    <div>
                      <Label>Upload Your CV *</Label>
                      <div className="mt-1">
                        <label 
                          htmlFor="cv-upload"
                          className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                            cvFile 
                              ? 'border-teal-400 bg-teal-50' 
                              : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400'
                          }`}
                        >
                          {cvFile ? (
                            <div className="text-center">
                              <CheckCircle2 className="h-10 w-10 text-teal-600 mx-auto mb-2" />
                              <p className="font-semibold text-teal-700">{cvFile.name}</p>
                              <p className="text-sm text-gray-500 mt-1">Click to change file</p>
                            </div>
                          ) : (
                            <div className="text-center">
                              <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                              <p className="font-semibold text-gray-700">Click to upload your CV</p>
                              <p className="text-sm text-gray-500 mt-1">PDF or Word document (max 10MB)</p>
                            </div>
                          )}
                          <input
                            id="cv-upload"
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                          />
                        </label>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white py-6 text-lg shadow-lg"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit My CV
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-center text-gray-500">
                      By submitting, you agree to our{' '}
                      <Link to="/privacy" className="text-teal-600 hover:underline">Privacy Policy</Link>
                      {' '}and consent to being contacted about opportunities.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default JoinTalentPool;
